import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  Timestamp,
} from "firebase/firestore";
import { db } from "src/firebase";
import {
  TaskFailure,
  TaskFailuresCursor,
  TaskFailuresPage,
} from "./types/task-failure.entity";

const PAGE_SIZE = 20;
const SOURCE_BATCH_SIZE = PAGE_SIZE + 1;

class TaskFailuresRepository {
  async find(offset?: TaskFailuresCursor): Promise<TaskFailuresPage> {
    const taskFailuresSnapshot = await getDocs(
      query(
        collection(db, "task_execution_exceptions"),
        orderBy("lastFailedAt", "desc"),
        orderBy(documentId(), "desc"),
        ...(offset
          ? [startAfter(Timestamp.fromMillis(offset.lastFailedAt), offset.id)]
          : []),
        limit(SOURCE_BATCH_SIZE)
      )
    );
    const taskFailureDocs = taskFailuresSnapshot.docs.slice(0, PAGE_SIZE);
    const items = taskFailureDocs.map((taskFailureDoc) => ({
      id: taskFailureDoc.id,
      ...(taskFailureDoc.data() as Omit<TaskFailure, "id">),
    }));

    return {
      items,
      nextOffset: this.getNextOffset(items, taskFailuresSnapshot.size),
    };
  }

  private getNextOffset(items: TaskFailure[], sourceSize: number) {
    const lastTaskFailure = items[items.length - 1];
    if (sourceSize <= PAGE_SIZE || !lastTaskFailure) return undefined;

    return {
      lastFailedAt: lastTaskFailure.lastFailedAt.toMillis(),
      id: lastTaskFailure.id,
    };
  }

  async findById(id: string): Promise<TaskFailure | undefined> {
    const taskFailureDoc = await getDoc(doc(db, "task_execution_exceptions", id));
    if (!taskFailureDoc.exists()) return undefined;

    return {
      id: taskFailureDoc.id,
      ...(taskFailureDoc.data() as Omit<TaskFailure, "id">),
    };
  }
}

export const taskFailuresRepository = new TaskFailuresRepository();
